import React, { useContext, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Image from '@atlaskit/image';
import type { TriggerProps } from '@atlaskit/popup';
import Popup from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';

import { SPAViewContext } from '@confluence/spa-view-context';
import { Attribution, withErrorBoundary } from '@confluence/error-boundary';

import { Card, CardContainer } from '../UpsellLooknFeel/Card';
import { ConfluenceEdition } from '../Upgrade/__types__/waitForEditionChangeQuery';
import { CardDialogDismissButton } from '../WhiteboardsUpsell/WhiteboardsUpsell';

import imgSrcDark from './images/cards-insert-upsell-dark.svg';
import imgSrcLight from './images/cards-insert-upsell-light.svg';

const i18n = defineMessages({
	title: {
		id: 'change-edition.company-hub-publish.upsell-title',
		defaultMessage: 'You’ve built something worth sharing',
		description: 'Highlighted title message for upsell popup.',
	},
	bodyAdmin: {
		id: 'change-edition.company-hub-publish.upsell-body-admin',
		defaultMessage:
			'Keep everyone at your company informed and up-to-date by activating this beautiful, dynamic hub today.',
		description: 'General message in the body for upsell popup for admin users.',
	},
	bodyNonAdmin: {
		id: 'change-edition.company-hub-publish.upsell-body-non-admin',
		defaultMessage:
			'Keep everyone at your company informed and up-to-date. Contact your admin about activating this beautiful, dynamic hub today.',
		description: 'General message in the body for upsell popup for non-admin users.',
	},
	exploreCompanyHub: {
		id: 'change-edition.company-hub-publish.explore-company-hub',
		defaultMessage: 'Explore Company Hub',
		description: 'Text for secondary CTA button to explore company hub.',
	},
	imageAltText: {
		id: 'change-edition.company-hub-publish.upsell-image-alt-text',
		defaultMessage: 'Company Hub publish upsell image',
		description: 'Image alt text for upsell popup.',
	},
});

const COMPANY_HUB_PUBLISH_UPSELL_SCREEN = 'companyHubPublishUpsell';
const EXPLORE_ADMIN_ANNOUNCEMENT_BANNER =
	'https://support.atlassian.com/confluence-cloud/docs/create-and-customize-a-company-hub/';

const LEARN_MORE_LINK = {
	href: EXPLORE_ADMIN_ANNOUNCEMENT_BANNER,
	actionSubjectId: 'companyHubPublishUpsellLearnMore',
	messageDescriptor: i18n.exploreCompanyHub,
};

const styles = xcss({
	width: '400px',
});

type CompanyHubPublishUpsellProps = {
	children: React.ReactNode;
	isEnabled: boolean;
};

const CompanyHubPublishUpsellComponent = ({
	children,
	isEnabled,
}: CompanyHubPublishUpsellProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const { isSiteAdmin } = useContext(SPAViewContext);
	const { formatMessage } = useIntl();

	if (!isEnabled) {
		return <>{children}</>;
	}

	const content = () => (
		<Box xcss={styles}>
			<CardContainer>
				<Card
					withTasteAndToneStyling
					title={formatMessage(i18n.title)}
					body={formatMessage(isSiteAdmin ? i18n.bodyAdmin : i18n.bodyNonAdmin)}
					source={COMPANY_HUB_PUBLISH_UPSELL_SCREEN}
					targetEdition={ConfluenceEdition.PREMIUM}
					upgradeEditionActionSubjectId="companyHubPublishUpsellButton"
					buttonGroupAlignment="right"
					learnMoreLink={LEARN_MORE_LINK}
					headerButton={
						<CardDialogDismissButton
							onClick={() => setIsOpen(false)}
							source={COMPANY_HUB_PUBLISH_UPSELL_SCREEN}
							actionSubjectId="dismissButton"
						/>
					}
				>
					<Image
						src={imgSrcLight}
						srcDark={imgSrcDark}
						alt={formatMessage(i18n.imageAltText)}
						width="100%"
					/>
				</Card>
			</CardContainer>
		</Box>
	);

	return (
		<Popup
			isOpen={isOpen}
			placement="bottom-end"
			content={content}
			trigger={(triggerProps: TriggerProps) => (
				<Box testId="publish-upsell-container" {...triggerProps} onClick={() => setIsOpen(!isOpen)}>
					{children}
				</Box>
			)}
		/>
	);
};

export const CompanyHubPublishUpsell = withErrorBoundary({
	attribution: Attribution.COMPANY_HUB,
})(CompanyHubPublishUpsellComponent);
